import type { FC } from 'react'
import { useEffect } from 'react'

import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import FormControl from '@mui/material/FormControl'
import Typography from '@mui/material/Typography'

import type {
  DisciplineVerzameling,
  UitCombiReden,
} from '@models/DisciplineVerzameling'

import { BooleanToggleGroup } from './BooleanToggleGroup'
import { SelectString } from './SelectString'

const uitCombiRedenen: UitCombiReden[] = [
  'Geen akkoord klant',
  'Geannuleerd door klant',
  'Technisch niet uitvoerbaar',
  'Verkeerd aangevraagd',
  'Uitvoering door netbeheerder zelf',
  'Buiten verzorgingsgebied netbeheerder',
  'Valt buiten scope',
]

interface Props {
  disciplineVerzamelingen: DisciplineVerzameling[]
  onChange: (disciplineVerzamelingen: DisciplineVerzameling[]) => void
  redenErrors: Record<string, string>
}

export const EditCombiUitvoeringPerDiscipline: FC<Props> = ({
  disciplineVerzamelingen,
  onChange,
  redenErrors,
}) => {
  const update = (
    dv: DisciplineVerzameling,
    updates: Partial<DisciplineVerzameling>,
  ) => {
    const updatedData = [...disciplineVerzamelingen]
    const index = disciplineVerzamelingen.findIndex(
      (d) =>
        d.disciplineCode === dv.disciplineCode &&
        d.netbeheerderCode === dv.netbeheerderCode,
    )

    // dv comes from this render's disciplineVerzamelingen.map.
    updatedData[index] = {
      ...updatedData[index],
      ...updates,
    }

    onChange(updatedData)
  }

  useEffect(() => {
    if (
      disciplineVerzamelingen.some(
        (dv) => typeof dv.isCombiUitvoering === 'undefined',
      )
    ) {
      const definedDiscVer = [...disciplineVerzamelingen].map((dv) => ({
        ...dv,
        isCombiUitvoering:
          typeof dv.isCombiUitvoering === 'undefined'
            ? true
            : dv.isCombiUitvoering,
      }))

      onChange(definedDiscVer)
    }
  }, [disciplineVerzamelingen, onChange])

  return (
    <>
      {disciplineVerzamelingen.map((dv) => (
        <Accordion
          key={`${dv.disciplineCode}-${dv.netbeheerderCode}`}
          expanded={dv.isCombiUitvoering === false}
          slotProps={{ transition: { unmountOnExit: true } }}
        >
          <AccordionSummary component={'div'}>
            <Typography style={{ flex: 1 }}>
              {dv.disciplineLabel} mee in combi
            </Typography>

            <BooleanToggleGroup
              value={dv.isCombiUitvoering}
              onChange={(bool) => update(dv, { isCombiUitvoering: bool })}
            />
          </AccordionSummary>

          <AccordionDetails>
            <FormControl fullWidth>
              <SelectString
                options={uitCombiRedenen}
                onChange={(reden) =>
                  update(dv, {
                    isCombiUitvoeringReden: reden || undefined,
                  })
                }
                value={dv.isCombiUitvoeringReden || null}
                required={!dv.isCombiUitvoering}
                error={
                  !!redenErrors[
                    `${dv.disciplineCode}-${dv.netbeheerderCode}-combiuitvoering`
                  ]
                }
                helperText={
                  redenErrors[
                    `${dv.disciplineCode}-${dv.netbeheerderCode}-combiuitvoering`
                  ] || ''
                }
                label="Reden"
              />
            </FormControl>
          </AccordionDetails>
        </Accordion>
      ))}
    </>
  )
}
